import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedIcon, IconName } from '../../components/icons';
import { adminService } from '../../services/adminService';
import { FlaggedMessage, CommissionDefault, SurveyorProfile } from '../../types';
import { toNumber } from '../../utils/number';

type Tab = 'flagged' | 'defaults' | 'suspended';

export const AdminAntiCheat: React.FC = () => {
  const [tab, setTab] = useState<Tab>('flagged');
  const [flagged, setFlagged] = useState<FlaggedMessage[]>([]);
  const [defaults, setDefaults] = useState<CommissionDefault[]>([]);
  const [suspended, setSuspended] = useState<SurveyorProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setIsLoading(true);
    setActionMsg('');
    try {
      const [fl, df, sv] = await Promise.all([
        adminService.listFlaggedMessages().then(r => r.results ?? []),
        adminService.listCommissionDefaults().then(r => r.results ?? []),
        adminService.listSurveyors({ verification_status: undefined }).then(r =>
          (r.results ?? []).filter(s => s.is_suspended)
        ),
      ]);
      setFlagged(fl);
      setDefaults(df);
      setSuspended(sv);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  const dismissFlag = async (id: string) => {
    try {
      await adminService.dismissFlaggedMessage(id);
      setFlagged(prev => prev.filter(f => f.id !== id));
      setActionMsg('✅ Message dismissed.');
    } catch { setActionMsg('❌ Failed to dismiss.'); }
  };

  const markPaid = async (dealId: string) => {
    try {
      await adminService.markCommissionPaid(dealId);
      setDefaults(prev => prev.filter(d => d.deal_id !== dealId));
      setActionMsg('✅ Commission marked as paid.');
    } catch { setActionMsg('❌ Failed to mark paid.'); }
  };

  const unsuspend = async (id: string) => {
    try {
      await adminService.unsuspendSurveyor(id);
      setSuspended(prev => prev.filter(s => s.id !== id));
      setActionMsg('✅ Surveyor reinstated.');
    } catch { setActionMsg('❌ Failed to reinstate.'); }
  };

  const tabs: { key: Tab; label: string; icon: IconName; count: number }[] = [
    { key: 'flagged',   label: 'Flagged Messages',     icon: 'warning', count: flagged.length },
    { key: 'defaults',  label: 'Commission Defaults',  icon: 'money',   count: defaults.length },
    { key: 'suspended', label: 'Suspended Surveyors',  icon: 'shield',  count: suspended.length },
  ];

  const isActionSuccess = actionMsg.startsWith('✅');

  return (
    <div>
      {/* Header */}
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>
            <span style={{ marginRight: 8, display: 'inline-flex', alignSelf: 'center' }}>
              <AnimatedIcon name="shield" size={22} color="var(--sc-text-secondary)" />
            </span>
            Anti-Cheat Monitor
          </h1>
          <p style={s.subtitle}>Platform integrity — flagged chats, commission enforcement, and suspensions.</p>
        </div>
        <button onClick={loadAll} style={s.refreshBtn}>↺ Refresh</button>
      </div>

      {/* KPI Strip */}
      <div style={s.kpiRow}>
        {[
          { label: 'Flagged Messages',    val: flagged.length,   color: 'var(--sc-error)', bg: 'var(--sc-error-bg)' },
          { label: 'Commission Defaults', val: defaults.length,  color: 'var(--sc-warning)', bg: 'var(--sc-warning-bg)' },
          { label: 'Suspended Surveyors', val: suspended.length, color: 'var(--sc-error)', bg: 'var(--sc-error-bg)' },
        ].map(k => (
          <div key={k.label} style={{ ...s.kpiCard, borderTop: `3px solid ${k.color}` }}>
            <div style={{ ...s.kpiVal, color: k.color }}>{k.val}</div>
            <div style={s.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Rules info card */}
      <div style={s.rulesCard}>
        <div style={s.rulesTitle}>🛡 Anti-Cheating Rules in Effect</div>
        <div style={s.rulesList}>
          {[
            ['1️⃣', 'Chat filter', 'Numbers, emails, and URLs are auto-blocked before deal confirmation'],
            ['2️⃣', 'Contact unlock', 'Full contact details revealed only after deal is confirmed'],
            ['3️⃣', 'Deal value gate', 'Surveyor must enter agreed deal value before confirmation proceeds'],
            ['4️⃣', 'Admin monitoring', 'All blocked messages logged here for review'],
            ['5️⃣', 'Reputation penalty', 'Points deducted on rule violations; reputation score drops'],
          ].map(([num, head, desc]) => (
            <div key={head as string} style={s.ruleRow}>
              <span style={s.ruleNum}>{num}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--sc-text-primary)' }}>{head as string}</div>
                <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>{desc as string}</div>
              </div>
            </div>
          ))}
          <div style={s.ruleRow}>
            <span style={s.ruleNum}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--sc-error)' }}>Commission default consequences</div>
              <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>
                Fails to pay → <strong>suspended</strong> → cannot access new jobs until commission cleared + reinstatement by admin
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action message */}
      {actionMsg && (
        <div style={{
          ...s.actionMsg,
          background: isActionSuccess ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)',
          color: isActionSuccess ? 'var(--sc-success-text)' : 'var(--sc-error-text)',
        }}>
          {actionMsg}
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ ...s.tab, ...(tab === t.key ? s.activeTab : {}) }}>
            <AnimatedIcon name={t.icon} size={15} color={tab === t.key ? 'var(--sc-text-inverse)' : 'var(--sc-text-secondary)'} /> {t.label}
            {t.count > 0 && (
              <span style={{
                ...s.badge,
                background: tab === t.key ? 'rgba(255,255,255,0.3)' : 'var(--sc-error-bg)',
                color: tab === t.key ? 'var(--sc-text-inverse)' : 'var(--sc-error-text)',
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : (
        <>
          {/* ── Flagged Messages ── */}
          {tab === 'flagged' && (
            flagged.length === 0
              ? <div style={s.empty}>✅ No flagged messages. Chat filter is clean.</div>
              : <div style={s.list}>
                {flagged.map(f => (
                  <div key={f.id} style={s.flagCard}>
                    <div style={s.flagHeader}>
                      <div>
                        <span style={s.flagUser}>{f.sender.first_name} {f.sender.last_name}</span>
                        <span style={s.flagMeta}> · Negotiation #{f.negotiation_id.slice(-6)}</span>
                      </div>
                      <span style={s.flagTime}>{new Date(f.created_at).toLocaleString()}</span>
                    </div>
                    <div style={s.flagContent}>"{f.content}"</div>
                    <div style={s.flagReason}>🚨 {f.blocked_reason}</div>
                    <div style={s.flagActions}>
                      <button onClick={() => dismissFlag(f.id)} style={s.dimissBtn}>✓ Dismiss</button>
                      <Link to={`/admin/negotiations/${f.negotiation_id}`} style={s.viewLink}>View Chat →</Link>
                    </div>
                  </div>
                ))}
              </div>
          )}

          {/* ── Commission Defaults ── */}
          {tab === 'defaults' && (
            defaults.length === 0
              ? <div style={s.empty}>✅ No commission defaults outstanding.</div>
              : <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <div style={s.table}>
                    <div style={s.thead}>
                      {['Surveyor', 'Deal Value', 'Commission Due', 'Days Overdue', 'Status', ''].map((h, i) => (
                        <div key={i} style={{ ...s.th, flex: h === '' ? 0.6 : 1 }}>{h}</div>
                      ))}
                    </div>
                    {defaults.map(d => (
                      <div key={d.deal_id} style={s.trow}>
                        <div style={{ ...s.td, flex: 1 }}>
                          <div style={{ fontWeight: 700, color: 'var(--sc-text-primary)' }}>{d.surveyor.first_name} {d.surveyor.last_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)' }}>{d.surveyor.base_city}</div>
                        </div>
                        <div style={{ ...s.td, flex: 1 }}>$ {(d.deal_value ?? 0).toFixed(2)}</div>
                        <div style={{ ...s.td, flex: 1, fontWeight: 700, color: 'var(--sc-error)' }}>$ {(d.expected_commission ?? 0).toFixed(2)}</div>
                        <div style={{ ...s.td, flex: 1 }}>
                          <span style={{ color: d.days_overdue > 14 ? 'var(--sc-error)' : 'var(--sc-warning)', fontWeight: 700 }}>
                            {d.days_overdue}d
                          </span>
                        </div>
                        <div style={{ ...s.td, flex: 1 }}>
                          <span style={{ ...s.statusBadge, ...(d.status === 'DEFAULTED' ? s.defaulted : s.overdue) }}>
                            {d.status}
                          </span>
                        </div>
                        <div style={{ ...s.td, flex: 0.6 }}>
                          <button onClick={() => markPaid(d.deal_id)} style={s.paidBtn}>✓ Mark Paid</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
          )}

          {/* ── Suspended Surveyors ── */}
          {tab === 'suspended' && (
            suspended.length === 0
              ? <div style={s.empty}>✅ No surveyors currently suspended.</div>
              : <div style={s.list}>
                {suspended.map(sv => (
                  <div key={sv.id} style={s.suspendCard}>
                    <div style={s.suspendLeft}>
                      <div style={s.avatar}>{sv.first_name?.[0] || '?'}{sv.last_name?.[0] || '?'}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--sc-text-primary)' }}>{sv.first_name} {sv.last_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>{sv.base_city} · {sv.total_jobs} jobs · ⭐ {toNumber(sv.rating_average).toFixed(1)}</div>
                        {sv.suspension_reason && (
                          <div style={{ fontSize: 12, color: 'var(--sc-error)', marginTop: 4, fontStyle: 'italic' }}>
                            Reason: {sv.suspension_reason}
                          </div>
                        )}
                        {sv.suspended_at && (
                          <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 2 }}>
                            Since {new Date(sv.suspended_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={s.suspendRight}>
                      <div style={s.repScore}>
                        <div style={{ fontSize: 11, color: 'var(--sc-text-secondary)', marginBottom: 4 }}>Reputation</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: sv.reputation_score < 40 ? 'var(--sc-error)' : 'var(--sc-warning)' }}>
                          {sv.reputation_score}/100
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)' }}>Penalty pts: {sv.penalty_points}</div>
                      </div>
                      <button onClick={() => unsuspend(sv.id)} style={s.reinstateBtn}>↩ Reinstate</button>
                    </div>
                  </div>
                ))}
              </div>
          )}
        </>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },
  refreshBtn: { padding: '9px 16px', background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', transition: 'opacity 0.2s' },

  kpiRow: { display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' },
  kpiCard: { flex: 1, minWidth: 150, background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: '16px 18px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', textAlign: 'center' as const },
  kpiVal: { fontSize: 24, fontWeight: 800, margin: '6px 0 2px', fontFamily: "'Outfit', sans-serif" },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },

  rulesCard: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', padding: '18px 22px', marginBottom: 22, boxShadow: 'var(--sc-shadow-md)' },
  rulesTitle: { fontSize: 15, fontWeight: 800, color: 'var(--sc-text-primary)', marginBottom: 14, fontFamily: "'Outfit', sans-serif" },
  rulesList: { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  ruleRow: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  ruleNum: { fontSize: 18, flexShrink: 0, marginTop: 1 },

  actionMsg: { borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, marginBottom: 14 },

  tabs: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const },
  tab: { padding: '8px 16px', border: '1px solid var(--sc-border)', borderRadius: 8, background: 'var(--sc-bg-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s' },
  activeTab: { background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', borderColor: 'var(--sc-brand-primary)' },
  badge: { borderRadius: 9, height: 18, minWidth: 18, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' },

  list: { display: 'flex', flexDirection: 'column' as const, gap: 12 },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', fontSize: 14, boxShadow: 'var(--sc-shadow-md)' },

  /* Flagged message cards */
  flagCard: { background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: '16px 20px', border: '1.5px solid var(--sc-error)', boxShadow: 'var(--sc-shadow-md)' },
  flagHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 },
  flagUser: { fontWeight: 700, fontSize: 14, color: 'var(--sc-text-primary)' },
  flagMeta: { fontSize: 12, color: 'var(--sc-text-tertiary)' },
  flagTime: { fontSize: 11, color: 'var(--sc-text-tertiary)' },
  flagContent: { background: 'var(--sc-bg-primary)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--sc-text-primary)', fontStyle: 'italic', marginBottom: 8, border: '1px solid var(--sc-border)' },
  flagReason: { fontSize: 12, color: 'var(--sc-error)', fontWeight: 600, marginBottom: 10 },
  flagActions: { display: 'flex', gap: 10, alignItems: 'center' },
  dimissBtn: { padding: '6px 14px', background: 'var(--sc-success-bg)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, color: 'var(--sc-success-text)', cursor: 'pointer' },
  viewLink: { fontSize: 12, color: 'var(--sc-brand-primary)', fontWeight: 600, textDecoration: 'none' },

  /* Commission table */
  table: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-md)', minWidth: 800 },
  thead: { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const },
  trow: { display: 'flex', padding: '13px 20px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  statusBadge: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 },
  overdue:   { background: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)' },
  defaulted: { background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)' },
  paidBtn: { padding: '6px 12px', background: 'var(--sc-success-bg)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, color: 'var(--sc-success-text)', cursor: 'pointer', whiteSpace: 'nowrap' as const },

  /* Suspended surveyor cards */
  suspendCard: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: '18px 22px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  suspendLeft: { display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, minWidth: 260 },
  suspendRight: { display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0, flexWrap: 'wrap' },
  avatar: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 },
  repScore: { textAlign: 'center' as const },
  reinstateBtn: { padding: '9px 18px', background: 'var(--sc-success)', border: 'none', borderRadius: 8, color: 'var(--sc-text-inverse)', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
};

